import * as React from 'react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  useDraftVendorReport,
  useVendorReportQuery,
  type ReportCitation,
} from '@/lib/api/vendor-intelligence-report';
import { cn } from '@/lib/utils/cn';
import { CitationDialog } from './citation-dialog';
import { DraftView } from './draft-view';
import { PublishedView } from './published-view';
import { prepareBlocker, type PrepareState, type ReportStepView } from './report-views';

export type { ReportStepView } from './report-views';

/**
 * Vendor intelligence → Report (T-0283; E24-design §5, §9, amended 4 Nov
 * 2026; ADR-026 decisions 5 and 9). The step opens on the published
 * report: the latest version's pages in the shared `PageCarousel`,
 * read-only, with Open PDF beside them. Drafting and editing are a second
 * view, reached with "Edit draft" (`?view=draft` on the step's URL) and
 * left with "Back to published" or by publishing. Before anything is
 * published the step is one box that prepares the first draft.
 *
 * The step owns the report read, which view shows, Prepare (offered by
 * both views) and the citation dialog; each view renders its own bar and
 * pages. Every number shown is the server's.
 */
export function ReportStep({
  engagementId,
  ratingsReady,
  engagement,
  shortlistReady = true,
  view,
  onView,
  className,
}: {
  engagementId: string;
  /** At least two vendors have confirmed ratings (the tab's own count). */
  ratingsReady: boolean;
  /** The engagement, for the cover's client line when a draft frozen under v1 carries none. */
  engagement?: { client_name: string; name: string } | null;
  /**
   * Every disposition is confirmed (the server's `dispositions_confirmed`,
   * T-0274; ADR-026 decision 3): the report's pages follow the shortlist,
   * so Prepare waits for it.
   */
  shortlistReady?: boolean;
  /** The view the URL names; left out, the step keeps it itself. */
  view?: ReportStepView;
  onView?: (view: ReportStepView) => void;
  className?: string;
}) {
  const query = useVendorReportQuery(engagementId);
  const report = query.data;
  const draftMutation = useDraftVendorReport(engagementId);
  // Controlled by the screen (the URL) when `onView` is given; otherwise
  // the step keeps the view itself, starting from `view` if one is passed.
  const [localView, setLocalView] = React.useState<ReportStepView>(view ?? 'published');
  const shownView = onView ? (view ?? 'published') : localView;
  const setView = onView ?? setLocalView;
  const [focus, setFocus] = React.useState('');
  const [notice, setNotice] = React.useState<string | null>(null);
  const [openCitation, setOpenCitation] = React.useState<ReportCitation | null>(null);

  const runDraft = () => {
    setNotice(null);
    const hadDraft = report?.draft != null;
    draftMutation.mutate(focus.trim() || null, {
      onSuccess: () => {
        setFocus('');
        setNotice(
          hadDraft
            ? 'Prepared again from the confirmed ratings. Paragraphs you edited or rewrote are kept.'
            : 'Drafted. Read it through page by page, edit or rewrite any paragraph, then publish.',
        );
        setView('draft');
      },
    });
  };

  const root = (children: React.ReactNode) => (
    <div
      data-testid="vi-report-step"
      className={cn('flex min-h-0 flex-1 flex-col gap-3 px-6 py-4', className)}
    >
      {children}
    </div>
  );

  if (!report) {
    return root(
      query.error ? (
        <ErrorMessage error={query.error} />
      ) : (
        <p className="m-0 flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner className="h-3.5 w-3.5" /> Loading the report…
        </p>
      ),
    );
  }

  const prepare: PrepareState = {
    run: runDraft,
    pending: draftMutation.isPending,
    error: draftMutation.error,
    focus,
    setFocus,
    blocker: prepareBlocker(report, ratingsReady, shortlistReady),
  };
  const hasPublished = report.versions.length > 0;
  // A reader who cannot edit sees the published report only.
  const draftMode = shownView === 'draft' && report.can_edit;

  return root(
    <>
      {draftMode ? (
        <DraftView
          engagementId={engagementId}
          report={report}
          engagement={engagement}
          prepare={prepare}
          notice={notice}
          hasPublished={hasPublished}
          onBack={() => {
            setNotice(null);
            setView('published');
          }}
          onPublished={() => {
            setNotice(null);
            setView('published');
          }}
          onOpenCitation={setOpenCitation}
        />
      ) : (
        <PublishedView
          engagementId={engagementId}
          report={report}
          engagement={engagement}
          prepare={prepare}
          onOpenDraft={() => setView('draft')}
          onOpenCitation={setOpenCitation}
        />
      )}
      <CitationDialog citation={openCitation} onClose={() => setOpenCitation(null)} />
    </>,
  );
}
